import { useEffect, useRef, useState } from 'react';
import {
  getGmbAuditHistory,
  getGmbAuditResult,
  type GmbAuditHistoryItem,
  type RetrievedGmbAuditReport,
} from '@/pages/gmb-audit/data/auditApi';
import { getHistoryScopeToken } from '@/pages/gmb-audit/utils/historyScope';
import type { SaveStatus } from '@/pages/gmb-audit/report/components/ReportSaveSection';
import HistoryComparisonSection from '@/pages/gmb-audit/report/components/HistoryComparisonSection';
import HistoryTrendChart, {
  buildTrendPoints,
} from '@/pages/gmb-audit/report/components/HistoryTrendChart';

type HistoryStatus = 'idle' | 'loading' | 'loaded' | 'error';
type ComparisonStatus = 'idle' | 'loading' | 'ready' | 'error';

interface ReportHistorySectionProps {
  /** 目前報告的 target placeId（用於推導匿名 historyScopeToken）。 */
  targetPlaceId: string;
  /** 保存狀態：成功後標記 history cache 為 stale（不自動重新 fetch）。 */
  saveStatus: SaveStatus;
  /** 「查看該次報告」→ 交由 page 以 getGmbAuditResult(reportToken) 讀回。 */
  onOpenReport: (reportToken: string) => void;
}

function mapHistoryError(code: string): string {
  switch (code) {
    case 'INVALID_HISTORY_SCOPE':
      return '歷史紀錄識別資料格式錯誤。';
    case 'INVALID_TARGET_PLACE_ID':
      return '商家識別資料格式錯誤。';
    case 'SERVER_DB_CREDENTIAL_UNAVAILABLE':
      return '目前無法連線至歷史紀錄服務，請稍後再試。';
    case 'RATE_LIMITED':
      return '目前使用人數較多，請稍後再試。';
    case 'RATE_LIMIT_UNAVAILABLE':
      return '服務暫時無法使用，請稍後再試。';
    default:
      return '讀取歷史紀錄失敗，請稍後再試。';
  }
}

function mapComparisonError(code: string): string {
  switch (code) {
    case 'REPORT_NOT_FOUND':
      return '找不到其中一份已保存的健診報告。';
    case 'REPORT_DATA_INTEGRITY_ERROR':
      return '其中一份報告的保存資料不完整，暫時無法比較。';
    case 'REPORT_SCHEMA_UNSUPPORTED':
      return '其中一份報告使用較舊或尚未支援的資料版本。';
    case 'SERVER_DB_CREDENTIAL_UNAVAILABLE':
      return '目前無法連線至報告讀取服務，請稍後再試。';
    case 'RATE_LIMITED':
      return '目前使用人數較多，請稍後再試。';
    case 'RATE_LIMIT_UNAVAILABLE':
      return '服務暫時無法使用，請稍後再試。';
    default:
      return '讀取比較資料失敗，請稍後再試。';
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}

/**
 * Batch 7-C｜此瀏覽器健診紀錄 + 趨勢 + 前後比較（UI-2C 整理）。
 * 預設不自動讀取：只有使用者主動點「查看歷史健診紀錄」才呼叫 Backend（click-to-load）。
 * 讀取/比較完全來自 Database Snapshot，不重新呼叫 Google / Peer / Scoring。
 */
export default function ReportHistorySection({
  targetPlaceId,
  saveStatus,
  onOpenReport,
}: ReportHistorySectionProps) {
  const [historyStatus, setHistoryStatus] = useState<HistoryStatus>('idle');
  const [records, setRecords] = useState<GmbAuditHistoryItem[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const hasLoadedRef = useRef(false);

  const [oldToken, setOldToken] = useState<string>('');
  const [newToken, setNewToken] = useState<string>('');
  const [comparisonStatus, setComparisonStatus] = useState<ComparisonStatus>('idle');
  const [comparisonOldReport, setComparisonOldReport] = useState<RetrievedGmbAuditReport | null>(null);
  const [comparisonNewReport, setComparisonNewReport] = useState<RetrievedGmbAuditReport | null>(null);
  const [comparisonError, setComparisonError] = useState<string | null>(null);

  // 換商家（placeId 改變）→ 清空上一商家的歷史與比較 state。
  useEffect(() => {
    hasLoadedRef.current = false;
    setHistoryStatus('idle');
    setRecords([]);
    setTruncated(false);
    setHistoryError(null);
    setStale(false);
    setOldToken('');
    setNewToken('');
    setComparisonStatus('idle');
    setComparisonOldReport(null);
    setComparisonNewReport(null);
    setComparisonError(null);
  }, [targetPlaceId]);

  // 保存成功後標記 stale（不自動 fetch），提示使用者可手動重新整理。
  useEffect(() => {
    if (saveStatus === 'success' && hasLoadedRef.current) {
      setStale(true);
    }
  }, [saveStatus]);

  const loadHistory = async () => {
    const token = getHistoryScopeToken(targetPlaceId);
    if (!token) return;
    setHistoryStatus('loading');
    setHistoryError(null);
    const res = await getGmbAuditHistory({ historyScopeToken: token, targetPlaceId });
    if (!res.ok) {
      setHistoryStatus('error');
      setHistoryError(mapHistoryError(res.code));
      return;
    }
    const list = res.data.audits;
    setRecords(list);
    setTruncated(res.data.truncated);
    hasLoadedRef.current = true;
    setStale(false);
    setHistoryStatus('loaded');
    // 預設比較：較新 = 最新（index 0），較早 = 前一筆（index 1）。
    if (list.length >= 2) {
      setNewToken(list[0].reportToken);
      setOldToken(list[1].reportToken);
    } else {
      setNewToken('');
      setOldToken('');
    }
    setComparisonStatus('idle');
    setComparisonOldReport(null);
    setComparisonNewReport(null);
    setComparisonError(null);
  };

  const runComparison = async () => {
    if (!oldToken || !newToken || oldToken === newToken) return;
    setComparisonStatus('loading');
    setComparisonError(null);
    const [oldRes, newRes] = await Promise.all([
      getGmbAuditResult(oldToken),
      getGmbAuditResult(newToken),
    ]);
    if (!oldRes.ok) {
      setComparisonStatus('error');
      setComparisonError(mapComparisonError(oldRes.code));
      return;
    }
    if (!newRes.ok) {
      setComparisonStatus('error');
      setComparisonError(mapComparisonError(newRes.code));
      return;
    }
    setComparisonOldReport(oldRes.data);
    setComparisonNewReport(newRes.data);
    setComparisonStatus('ready');
  };

  const canLoad = historyStatus === 'idle' || historyStatus === 'error';
  const showRecords = historyStatus === 'loaded';
  const sameReportSelected = Boolean(oldToken) && oldToken === newToken;

  // 趨勢條件：同一 placeId（本區塊即單一 placeId）+ 至少 2 筆 + scoringModelVersion 相同。
  const sameScoringModel =
    records.length >= 2 && records.every((r) => r.scoringModelVersion === records[0].scoringModelVersion);
  const trendPoints = buildTrendPoints(records);
  const showTrend = sameScoringModel && trendPoints.length >= 2;
  const modelMismatch = records.length >= 2 && !sameScoringModel;

  return (
    <section className="ds-container pt-10 md:pt-12">
      <div className="rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-200">
              <i className="ri-history-line text-lg" />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-foreground-950 md:text-xl">此瀏覽器健診紀錄</h2>
              <p className="mt-1.5 text-base leading-relaxed text-foreground-600">
                查看這個商家過去已保存的健診結果，追蹤公開資料與同業表現的變化。
              </p>
              <p className="mt-1.5 text-base leading-relaxed text-foreground-600">
                這些紀錄以目前瀏覽器為單位保存，不是會員帳號或 Google 帳號紀錄。
              </p>
              <p className="mt-1 text-sm leading-relaxed text-foreground-400">
                清除瀏覽器資料、使用其他瀏覽器或更換裝置後，可能無法自動找回先前紀錄。
              </p>
            </div>
          </div>

          {canLoad && (
            <button
              type="button"
              onClick={loadHistory}
              className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 sm:w-auto"
            >
              <i className="ri-history-line" />
              查看歷史健診紀錄
            </button>
          )}

          {historyStatus === 'loading' && (
            <button
              type="button"
              disabled
              className="inline-flex h-14 w-full cursor-not-allowed items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700/70 px-6 text-base font-semibold text-background-50 sm:w-auto"
            >
              <i className="ri-loader-4-line animate-spin" />
              正在讀取…
            </button>
          )}

          {showRecords && (
            <button
              type="button"
              onClick={loadHistory}
              className="inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-background-400 bg-background-50 px-5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:w-auto"
            >
              <i className="ri-refresh-line" />
              重新整理紀錄
            </button>
          )}
        </div>

        {historyStatus === 'error' && historyError && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-rose-700">
            <i className="ri-error-warning-line mt-0.5 text-lg" />
            <p className="text-sm md:text-base">{historyError}</p>
          </div>
        )}

        {showRecords && stale && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-50/70 p-3.5 text-accent-900">
            <i className="ri-information-line mt-0.5 text-lg" />
            <p className="text-sm md:text-base">已保存新的健診結果，可點「重新整理紀錄」查看最新變化。</p>
          </div>
        )}

        {showRecords && records.length === 0 && (
          <div className="mt-4 rounded-2xl border border-dashed border-background-400 bg-background-100 p-6 text-center">
            <i className="ri-inbox-2-line text-2xl text-foreground-400" />
            <p className="mt-2 text-sm text-foreground-600 md:text-base">這個商家目前還沒有已保存的健診紀錄。</p>
            <p className="mt-1 text-sm text-foreground-400">完成健診並保存後，就會出現在這裡。</p>
          </div>
        )}

        {showRecords && records.length === 1 && (
          <div className="mt-4 rounded-2xl border border-dashed border-background-400 bg-background-100 p-6 text-center">
            <i className="ri-file-list-3-line text-2xl text-foreground-400" />
            <p className="mt-2 text-sm text-foreground-600 md:text-base">
              目前只有 1 筆已保存健診。
              <br className="hidden sm:block" />
              完成下一次健診並保存後，即可比較前後變化。
            </p>
          </div>
        )}

        {showRecords && records.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-2xl border border-background-200">
            <div className="hidden bg-background-100 px-4 py-2.5 text-sm font-semibold text-foreground-500 md:grid md:grid-cols-12">
              <span className="md:col-span-3">健診日期</span>
              <span className="md:col-span-3">健康度分數</span>
              <span className="md:col-span-3">等級</span>
              <span className="md:col-span-2">資料覆蓋率</span>
              <span className="md:col-span-1" />
            </div>
            <div className="divide-y divide-background-200">
              {records.map((item) => (
                <div
                  key={item.reportToken}
                  className="grid grid-cols-1 gap-2 px-4 py-3 md:grid-cols-12 md:items-center"
                >
                  <span className="text-sm font-medium text-foreground-900 md:col-span-3">
                    {formatDate(item.createdAt)}
                  </span>
                  <span className="text-sm text-foreground-700 md:col-span-3">
                    <span className="mr-1 text-sm text-foreground-400 md:hidden">健康度分數：</span>
                    {item.score === null ? '未計分' : `${item.score} / 100`}
                  </span>
                  <span className="text-sm text-foreground-700 md:col-span-3">
                    <span className="mr-1 text-sm text-foreground-400 md:hidden">等級：</span>
                    {item.healthGrade ?? '—'}
                    {item.healthLabel ? `｜${item.healthLabel}` : ''}
                  </span>
                  <span className="text-sm text-foreground-700 md:col-span-2">
                    <span className="mr-1 text-sm text-foreground-400 md:hidden">資料覆蓋率：</span>
                    {item.dataCoveragePercent === null ? '—' : `${item.dataCoveragePercent}%`}
                  </span>
                  <span className="md:col-span-1 md:text-right">
                    <button
                      type="button"
                      onClick={() => onOpenReport(item.reportToken)}
                      className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-3.5 text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-50 md:w-auto"
                    >
                      <i className="ri-file-search-line" />
                      查看報告
                    </button>
                  </span>
                </div>
              ))}
            </div>
            {truncated && (
              <div className="border-t border-background-200 bg-background-100 px-4 py-2 text-sm text-foreground-500">
                僅顯示最近 20 筆紀錄。
              </div>
            )}
          </div>
        )}

        {/* 模型守門：版本不同時不比較分數變化 */}
        {showRecords && modelMismatch && (
          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-50/70 p-3.5 text-accent-900">
            <i className="ri-alert-line mt-0.5 text-lg" />
            <p className="text-sm md:text-base">評分模型版本不同，不直接比較分數變化。</p>
          </div>
        )}

        {/* 健康度趨勢（同 placeId / ≥2 筆 / scoringModelVersion 相同） */}
        {showRecords && showTrend && <HistoryTrendChart points={trendPoints} />}

        {/* 前後比較：僅在 >= 2 筆時提供 */}
        {showRecords && records.length >= 2 && (
          <div className="mt-4 rounded-2xl border border-background-200 bg-background-100 p-4 md:p-5">
            <div className="flex items-center gap-2">
              <i className="ri-arrow-left-right-line text-primary-700" />
              <h3 className="text-base font-bold text-foreground-950 md:text-lg">前後比較</h3>
            </div>
            <p className="mt-1 text-sm text-foreground-500">任選兩次已保存的健診，比較公開資料與同業表現的變化。</p>

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-foreground-600">較早紀錄</span>
                <select
                  value={oldToken}
                  onChange={(e) => {
                    setOldToken(e.target.value);
                    setComparisonStatus('idle');
                  }}
                  className="cursor-pointer rounded-xl border border-background-400 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 outline-none focus:ring-2 focus:ring-primary-300"
                >
                  {records.map((item) => (
                    <option key={item.reportToken} value={item.reportToken}>
                      {formatDate(item.createdAt)}・{item.score === null ? '未計分' : `${item.score} / 100`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-foreground-600">較新紀錄</span>
                <select
                  value={newToken}
                  onChange={(e) => {
                    setNewToken(e.target.value);
                    setComparisonStatus('idle');
                  }}
                  className="cursor-pointer rounded-xl border border-background-400 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 outline-none focus:ring-2 focus:ring-primary-300"
                >
                  {records.map((item) => (
                    <option key={item.reportToken} value={item.reportToken}>
                      {formatDate(item.createdAt)}・{item.score === null ? '未計分' : `${item.score} / 100`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {sameReportSelected && (
              <p className="mt-2 text-sm text-rose-600">請選擇兩份不同的報告進行比較。</p>
            )}

            <button
              type="button"
              onClick={runComparison}
              disabled={comparisonStatus === 'loading' || sameReportSelected || !oldToken || !newToken}
              className="mt-3 inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {comparisonStatus === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  正在讀取比較資料…
                </>
              ) : (
                <>
                  <i className="ri-arrow-left-right-line" />
                  開始前後比較
                </>
              )}
            </button>

            {comparisonStatus === 'error' && comparisonError && (
              <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-rose-700">
                <i className="ri-error-warning-line mt-0.5 text-lg" />
                <p className="text-sm md:text-base">{comparisonError}</p>
              </div>
            )}
          </div>
        )}

        {comparisonStatus === 'ready' && comparisonOldReport && comparisonNewReport && (
          <HistoryComparisonSection oldReport={comparisonOldReport} newReport={comparisonNewReport} />
        )}
      </div>
    </section>
  );
}